import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import {
    AzavistaApiService, IGetReportRequest, IGetReportResponse, IEvent, IGetStagesRequest
} from '@azavista/servicelib';
import { AbstractWidgetDirective, IWidgetComponentDefinition } from '@azavista/components/widgets';
import {
    AzavistaSharedService, IField, IFilter,
    IValueChangedEventArgs, FieldComponentType, IObjectEnumInputFieldValue, IFieldIdWithOptions, IValueTranslation
} from '@azavista/components/shared';
import { AzavistaFilterCriteriaComponent, IFilterCriteriaData } from '@azavista/components/filter-criteria';
import { ParticipantsByTypeWidgetService } from './participants-by-type-widget.service';
import { SharedService, IWidgetDataChanges, TranslationKey, TRANSLATION_MAP } from '../../../shared';
import { IParticipantGroupBySettings, IParticipantGroupByPayload, IParticipantGroupBySettingsRemote, IRatingIcon } from './interfaces';
import { EventsService } from '../../../events/events.service';
import { WidgetsUtilsService } from '../widgets-utils.service';
import { WidgetViewDetailsNavigationPaths } from '../interfaces';
import { IDataPointSelectedArguments } from '../apexcharts-wrapper/interfaces';
import { WidgetCustomEventType } from '../enums';
import { CommonDialogsService } from '../../common-dialogs.service';
import { ChartType } from '../apextypes';

@Component({
    templateUrl: 'participants-by-type-widget.component.html',
    styleUrls: ['participants-by-type-widget.component.css'],
    standalone: false
})
export class ParticipantsByTypeWidgetComponent extends AbstractWidgetDirective implements OnInit, OnDestroy {
    chart: ApexChart = {
        type: 'donut'
    };
    series: ApexAxisChartSeries | ApexNonAxisChartSeries;
    title: ApexTitleSubtitle;
    labels: string[];
    allEventParticipantFields: IField[];
    enumTypeEventParticipantFields: IField[];
    definitionSettings: IParticipantGroupBySettings;
    columns: IField[];

    isEventPredefined = false;
    event: IEvent;
    eventNotAvailable = false;
    field: IField;
    isFieldRatingOrOpinionScale = false;
    ratingIcons: IRatingIcon[];
    groupByField: IField;
    groupByFieldValue: IObjectEnumInputFieldValue;

    eventId = '';
    translations = TRANSLATION_MAP;
    private destroyed$ = new Subject<void>();

    constructor(
        private apiSvc: AzavistaApiService,
        private azavistaSharedSvc: AzavistaSharedService,
        private participantsByTypeWidgetSvc: ParticipantsByTypeWidgetService,
        private matDialog: MatDialog,
        private sharedSvc: SharedService,
        private eventsSvc: EventsService,
        private widgetsUtilsSvc: WidgetsUtilsService,
        private commonDlgsSvc: CommonDialogsService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.definitionSettings = this.definition.settings as IParticipantGroupBySettings;
        this.definition.data = null;
        this.eventId = this.definition.context?.eventId;
        if (this.eventId) {
            // The widget is inside event context
            await this.setEventFromContext(this.eventId);
        } else {
            this.eventId = this.definition.settings.remote.event_id;
            if (this.eventId) {
                // Load event so we know whether it exists
                try {
                    this.event = await this.apiSvc.getEvent(this.eventId);
                    this.eventNotAvailable = false;
                } catch (err) {
                    this.eventNotAvailable = true;
                }
            }
        }
        if (this.definitionSettings.remote?.field_name) {
            try {
                this.field = await this.apiSvc.getEventParticipantField(this.definitionSettings.remote.field_name);
                this.isFieldRatingOrOpinionScale = this.field.type === 'rating' || this.field.type === 'opinion-scale';
            } catch (err) {
            }
        }
        this.setCustomWidgetParams();

        this.chart = {
            type: this.definitionSettings && this.definitionSettings.local.chartType
                ? this.definitionSettings.local.chartType
                : this.chart.type,
            toolbar: { show: false },
            zoom: { enabled: false },
            height: 200,
            animations: { enabled: true },
        };

        super.subscribeToDataChanges({
            next: (newData?: IWidgetDataChanges) => {
                this.dataChanged(newData);
            }
        });

        if (this.configMode) {
            if (this.eventId) {
                // Initially add only the selected event as item in event autocomplete
                this.event ||= await this.widgetsUtilsSvc.getEvent(this.eventId);
            }
            this.setDefaultRemoteSettingsIfMissing();
            await this.setEventParticipantFields();
            this.onSettingsChanged();
        } else {
            const hasRemoteSettings = !this.areRemoteSettingsEmpty(this.definitionSettings.remote);
            if (hasRemoteSettings) {
                // Start loading on next loop because it causes loading output to be emitted which could cause
                // Angular change detection errors in the consumer (the dashboard component) if it changes template bound variables
                // in the event handler
                window.setTimeout(() => this.loadWidgetData(this.definition));
            } else {
                // Notify parent that we need remote settings
                this.settingsNeeded.next();
            }
        }

        if (!this.definitionSettings.local.chartType) {
            this.definitionSettings.local.chartType = this.chart.type as any;
        }
        const { series, labels } = this.widgetsUtilsSvc.convertDataToSeriesAndLabels(this.definition.data);
        this.series = this.widgetsUtilsSvc.convertSeries(this.chart.type, series);
        this.labels = labels;
        this.createAzavistaFields();
    }

    createAzavistaFields(): void {
        this.groupByField = {
            name: 'group_by_field', label: this.sharedSvc.translate(TranslationKey.groupBy),
            editable: true, mandatory_for_planners: false,
            schema: { type: 'object' }, component: FieldComponentType.objectEnum
        } as IField;
        const selected = this.enumTypeEventParticipantFields?.find(x => x.id === this.definition.settings.remote.field_name);
        this.groupByFieldValue = {
            displayProperty: 'label', items: this.enumTypeEventParticipantFields || [],
            selected, reportProperty: 'id'
        };
    }

    onDataPointSelected(args: IDataPointSelectedArguments): void {
        this.customEvent.emit({ type: WidgetCustomEventType.dataPointSelected, data: args });
    }

    setCustomWidgetParams(): void {
        this.definition.viewButtonText = this.sharedSvc.translate(TranslationKey.goToEvent);
        this.definition.viewDetailsNavPath = WidgetViewDetailsNavigationPaths.event;
        this.definition.viewDetailsNavQueryParams = this.widgetsUtilsSvc.getViewDetailsNavQueryParamsForEvent(this.eventId);
        this.onSettingsChanged();
    }

    areRemoteSettingsEmpty(settings: IParticipantGroupBySettingsRemote): boolean {
        if (!settings || !settings.event_id || !settings.field_name) {
            return true;
        }
        return false;
    }

    async setEventFromContext(contextEventId: string): Promise<void> {
        this.isEventPredefined = true;
        this.definitionSettings.remote.event_id = contextEventId;
        try {
            this.event = await this.widgetsUtilsSvc.getEvent(contextEventId);
            this.eventNotAvailable = false;
        } catch (err) {
            this.eventNotAvailable = true;
        }
    }

    async onSelectEvent(): Promise<void> {
        const title = this.sharedSvc.translate(TranslationKey.events);
        const dlgResult = await this.commonDlgsSvc.showSelectEventsDialog(title, true);
        if (!dlgResult || dlgResult.length === 0) {
            return;
        }
        this.event = dlgResult[0] as IEvent;
        const eventId = this.event ? this.event.id : null;
        this.definitionSettings.remote.event_id = eventId;
        this.eventId = eventId;
        this.setCustomWidgetParams();
        await this.setEventParticipantFields();
        this.onSettingsChanged();
    }

    async setEventsData(): Promise<void> {
        await this.eventsSvc.loadGlobalEventFields();
        this.columns = this.sharedSvc.clone(this.eventsSvc.getEventsColumns(true));
    }

    async setEventParticipantFields(): Promise<void> {
        if (!this.eventId) {
            this.enumTypeEventParticipantFields = [];
            return;
        }
        this.enumTypeEventParticipantFields = await this.getEnumTypeGlobalEventFields(this.eventId);
        this.createAzavistaFields();
    }

    setDefaultRemoteSettingsIfMissing(): void {
        if (!this.definitionSettings.remote) {
            this.definitionSettings.remote = { participant_filter: {}, field_name: '', event_id: this.eventId };
        }
        if (!this.definitionSettings.remote.participant_filter) {
            this.definitionSettings.remote.participant_filter = {};
        }
    }

    loadWidgetData(definition: IWidgetComponentDefinition): void {
        this.definitionSettings = definition.settings;
        if (definition.size?.height) {
            this.chart.height = definition.size.height;
            this.chart.width = definition.size.width;
        }
        if (!this.definitionSettings.remote.event_id) {
            return;
        }
        const req: IGetReportRequest = {
            reportType: definition.reportType,
            widgetId: definition.id,
            payload: {
                participant_filter: this.definitionSettings.remote.participant_filter,
                field_name: this.definitionSettings.remote.field_name,
                event_id: this.definitionSettings.remote.event_id
            } as IParticipantGroupByPayload
        };
        this.notifyLoading(true);
        this.apiSvc.getReport(req)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: response => this.processDataLoaded(definition, response),
                error: () => this.processDataLoadedError()
            });
    }

    processDataLoaded(definition: IWidgetComponentDefinition, response: IGetReportResponse): void {
        this.notifyLoading(this.sharedSvc.getLoadingOnReportLoaded(response));
        definition.data = response.data;
        definition.dataChangedSubject.next({ data: definition.data });
    }

    processDataLoadedError(): void {
        // TODO: What if report data fails ?
        this.notifyLoading(false);
    }

    async addStageAsFilterField(dialogData: IFilterCriteriaData): Promise<void> {
        if (!this.eventId) {
            return;
        }
        const req: IGetStagesRequest = { parentType: 'event', parentID: this.eventId, offset: 0, limit: 50 };
        const stagesRes = await this.apiSvc.getStages(req);
        const stages = stagesRes.stages;
        if (!dialogData.allFields.some(x => x.id === 'stage_id')) {
            dialogData.allFields.push({
                id: 'stage_id', name: 'stage_id', label: this.sharedSvc.translate(TranslationKey.stage),
                editable: true, schema: { type: 'string', enum: stages.map(x => x.id) }
            } as IField);
        }
        const currentUserLanguage = this.apiSvc.getCurrentUser().language;
        const stagesMap: IValueTranslation = {};
        stages.forEach(x => stagesMap[x.id] = x.name);
        const stageFieldOptions: IFieldIdWithOptions = {
            fieldId: 'stage_id',
            options: {
                translations: {
                    [currentUserLanguage]: stagesMap
                }
            }
        };
        const fieldsOptions: IFieldIdWithOptions[] = [stageFieldOptions];
        dialogData.fieldsOptions = fieldsOptions;
    }

    async onShowFilter(): Promise<void> {
        const dialogData: IFilterCriteriaData = {
            allFilterNames: [],
            allFields: this.allEventParticipantFields,
            filter: { id: 'dummy-id', name: 'dummy-name', criteria: [] },
            isNew: true, hideFilterName: true,
            searchParams: this.definitionSettings.remote.participant_filter || {}
        } as IFilterCriteriaData;
        await this.addStageAsFilterField(dialogData);
        const dialogRef = this.matDialog.open(AzavistaFilterCriteriaComponent, {
            data: dialogData,
            panelClass: 'filter-criteria-dialog',
        });
        const deleteSubscription = dialogRef.componentInstance.deleted.subscribe({
            next: () => {
                deleteSubscription.unsubscribe();
                this.definitionSettings.remote.participant_filter = {};
                dialogRef.close();
            }
        });
        dialogRef.afterClosed().subscribe({
            next: dialogResult => {
                deleteSubscription.unsubscribe();
                this.processEditFilterDialogResult(dialogResult);
            }
        });

    }

    processEditFilterDialogResult(dialogResult: IFilter): void {
        if (!dialogResult) {
            return;
        }
        const searchParams = this.sharedSvc.createSearchParams(dialogResult.criteria);
        this.definitionSettings.remote.participant_filter = searchParams;
    }

    chartTypeChanged(newType: string): void {
        this.chart.type = newType as ChartType;
        const { series } = this.widgetsUtilsSvc.convertDataToSeriesAndLabels(this.definition.data);
        this.series = this.widgetsUtilsSvc.convertSeries(this.chart.type, series);
    }

    dataChanged(newData?: IWidgetDataChanges): void {
        let chartTypeChanged = false;
        let shouldConvertSeries = false;

        const settings = newData.settings as IParticipantGroupBySettings;
        if (settings?.remote?.event_id) {
            // Settings were changed - assume event is selected - clear eventNotAvailableFlag
            // TODO: This not always reflects event existence because if no event is selected in the settings
            //       the event_id still has value - the one of possibly non-existing event
            this.eventNotAvailable = false;
        }

        if (newData.settings && newData.settings.local) {
            if (this.chart.type !== newData.settings.local.chartType) {
                this.chart.type = newData.settings.local.chartType;
                chartTypeChanged = true;
            }
        }
        if (newData.data) {
            shouldConvertSeries = true;
        }
        if (newData.width && this.chart.width !== newData.width) {
            this.chart.width = newData.width;
        }
        if (newData.height && this.chart.height !== newData.height) {
            this.chart.height = newData.height;
            if (this.isFieldRatingOrOpinionScale) {
                // TODO: Reduce the height so the rating value is shown below
                this.chart.height -= 40;
            }
        }
        if (chartTypeChanged || shouldConvertSeries) {
            if (this.isFieldRatingOrOpinionScale) {
                const sumAndCount = this.getRatingSumAndCount(newData.data);
                this.ratingIcons = this.participantsByTypeWidgetSvc.generateRatingIcons(
                    +this.field.schema.minimum, +this.field.schema.maximum, sumAndCount.sum / sumAndCount.count
                );
            }
            const { series, labels } = this.widgetsUtilsSvc.convertDataToSeriesAndLabels(newData.data);
            this.series = this.widgetsUtilsSvc.convertSeries(this.chart.type, series);
            this.labels = labels;
        }
        if (chartTypeChanged) {
            this.chartTypeChanged(newData.settings.local.chartType);
        }
        if (!this.configMode && newData.settings && newData.settings.remote) {
            // Not config mode and remote settings were changed - reload data from server
            this.loadWidgetData(this.definition);
        }
    }

    getRatingSumAndCount(data: Record<string, number>): { sum: number, count: number } {
        const values = Object.values(data);
        let sum = 0;
        for (const value of values) {
            sum += +value || 0;
        }
        return { sum: sum, count: values.length };
    }

    onSettingsChanged(): void {
        const settingsAreValid = this.participantsByTypeWidgetSvc.areSettingsValid(this.definitionSettings);
        this.settingsChanged.next({
            definition: this.definition,
            settings: this.definitionSettings,
            settingsAreValid: settingsAreValid
        });
    }

    onGroupByFieldChanged(data: IValueChangedEventArgs): void {
        this.definition.settings.remote.field_name = data.value;
        this.onSettingsChanged();
    }

    private async getEnumTypeGlobalEventFields(eventId: string): Promise<IField[]> {
        this.allEventParticipantFields = await this.apiSvc.getAllEventParticipantFields(eventId);
        this.enumTypeEventParticipantFields = this.azavistaSharedSvc.getSelectAndMultiSelectFields(this.allEventParticipantFields);
        // Also add opinion-scale and rating fields
        this.enumTypeEventParticipantFields.push(...this.allEventParticipantFields.filter(x => x.type === 'opinion-scale' || x.type === 'rating'));
        return this.enumTypeEventParticipantFields;
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
        super.destroy();
    }
}
